<template>
  <div class="admin-page">
    <div class="admin-toolbar">
      <div class="admin-toolbar__actions">
        <el-input v-model="filters.referrerUserId" placeholder="推荐人ID" clearable class="filter-input" />
        <el-input v-model="filters.refereeUserId" placeholder="被推荐人ID" clearable class="filter-input" />
        <el-input v-model="filters.couponNo" placeholder="券号" clearable class="filter-input" />
        <el-button type="primary" @click="fetchBindings">查询绑定</el-button>
        <el-button @click="fetchCommission">分润流水</el-button>
        <el-button link type="primary" @click="$router.push('/admin/referral-user-overview')">用户全景</el-button>
      </div>
    </div>

    <el-tabs v-model="activeTab">
      <el-tab-pane label="绑定记录" name="bindings">
        <el-table :data="bindings" v-loading="loadingBindings">
          <el-table-column prop="id" label="ID" width="70" />
          <el-table-column prop="couponNo" label="券号" width="130" />
          <el-table-column prop="couponType" label="类型" width="120">
            <template #default="s">{{ couponTypeLabel(s.row.couponType) }}</template>
          </el-table-column>
          <el-table-column label="推荐人" min-width="120">
            <template #default="s">{{ s.row.referrerNickname }} ({{ s.row.referrerUserId }})</template>
          </el-table-column>
          <el-table-column label="被推荐人" min-width="120">
            <template #default="s">{{ s.row.refereeNickname || s.row.refereeUsername }} ({{ s.row.refereeUserId }})</template>
          </el-table-column>
          <el-table-column prop="bonusPaid" label="已赠金" width="80">
            <template #default="s">{{ s.row.bonusPaid === 1 ? '是' : '否' }}</template>
          </el-table-column>
          <el-table-column label="操作" width="160" fixed="right">
            <template #default="s">
              <el-button v-if="s.row.bonusPaid !== 1" link type="primary" @click="retryBonus(s.row)">补发礼遇</el-button>
              <el-button link type="danger" @click="revokeBinding(s.row)">撤销</el-button>
            </template>
          </el-table-column>
        </el-table>
        <AdminPagination
          v-model:current-page="bindingsPage"
          v-model:page-size="bindingsPageSize"
          :total="bindingsTotal"
          @size-change="fetchBindings"
          @current-change="fetchBindings"
        />
      </el-tab-pane>
      <el-tab-pane label="合伙人分润" name="commission">
        <el-table :data="commissions" v-loading="loadingCommission">
          <el-table-column prop="orderNo" label="订单号" min-width="160" show-overflow-tooltip />
          <el-table-column prop="partnerUserId" label="合伙人" width="90" />
          <el-table-column prop="orderAmountCents" label="订单(分)" width="100" />
          <el-table-column prop="commissionCents" label="分润(分)" width="100" />
          <el-table-column prop="status" label="状态" width="90">
            <template #default="s">{{ commissionStatusLabel(s.row.status) }}</template>
          </el-table-column>
        </el-table>
      </el-tab-pane>
      <el-tab-pane label="操作审计" name="audit">
        <el-table :data="auditItems" v-loading="loadingAudit">
          <el-table-column prop="id" label="ID" width="70" />
          <el-table-column prop="action" label="操作" width="140" />
          <el-table-column prop="adminUserId" label="管理员" width="90" />
          <el-table-column prop="targetUserId" label="目标用户" width="90" />
          <el-table-column prop="detail" label="详情" min-width="200" show-overflow-tooltip />
          <el-table-column label="时间" width="170">
            <template #default="s">{{ formatTime(s.row.createdAt) }}</template>
          </el-table-column>
        </el-table>
        <AdminPagination
          v-model:current-page="auditPage"
          v-model:page-size="auditPageSize"
          :total="auditTotal"
          @size-change="fetchAudit"
          @current-change="fetchAudit"
        />
      </el-tab-pane>
    </el-tabs>

    <div class="partner-grant">
      <h4>臻享资格（仅管理员）</h4>
      <p class="partner-grant__hint">撤销绑定后用户将被标记为不可再绑礼券；已发礼遇需人工核对。</p>
      <el-input v-model="partnerUserId" placeholder="用户 ID" style="width: 140px; margin-right: 8px" />
      <el-input v-model="partnerUsername" placeholder="或用户名" style="width: 160px; margin-right: 8px" />
      <el-button type="warning" @click="grantPartner(true)">开通臻享</el-button>
      <el-button @click="grantPartner(false)">关闭臻享</el-button>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import AdminPagination from '@/components/AdminPagination.vue'
import {
  adminRetryReferralBonus,
  commissionStatusLabel,
  couponTypeLabel,
  fetchAdminReferralActions
} from '@/utils/referralCoupon'

export default {
  name: 'ReferralBindingsManagement',
  components: { AdminPagination },
  data() {
    return {
      activeTab: 'bindings',
      loadingBindings: false,
      loadingCommission: false,
      loadingAudit: false,
      bindings: [],
      bindingsTotal: 0,
      bindingsPage: 1,
      bindingsPageSize: 20,
      commissions: [],
      auditItems: [],
      auditTotal: 0,
      auditPage: 1,
      auditPageSize: 20,
      filters: { referrerUserId: '', refereeUserId: '', couponNo: '' },
      partnerUserId: '',
      partnerUsername: ''
    }
  },
  watch: {
    activeTab(val) {
      if (val === 'audit' && !this.auditItems.length) this.fetchAudit()
    }
  },
  created() {
    this.fetchBindings()
  },
  methods: {
    couponTypeLabel,
    commissionStatusLabel,
    formatTime(ts) {
      if (!ts) return '—'
      return new Date(ts * 1000).toLocaleString()
    },
    async fetchBindings() {
      this.loadingBindings = true
      try {
        const res = await axios.post('/api/v1/admin/referral/bindings/list', {
          page: this.bindingsPage,
          pageSize: this.bindingsPageSize,
          referrerUserId: Number(this.filters.referrerUserId) || undefined,
          refereeUserId: Number(this.filters.refereeUserId) || undefined,
          couponNo: this.filters.couponNo || undefined
        })
        if (res.data.code === 200) {
          this.bindings = res.data.data?.bindings || []
          this.bindingsTotal = res.data.data?.total || 0
        }
      } finally {
        this.loadingBindings = false
      }
    },
    async fetchCommission() {
      this.activeTab = 'commission'
      this.loadingCommission = true
      try {
        const res = await axios.post('/api/v1/admin/referral/commission/list', {
          page: 1,
          pageSize: 50,
          partnerUserId: Number(this.filters.referrerUserId) || undefined
        })
        if (res.data.code === 200) {
          this.commissions = res.data.data?.items || []
        }
      } finally {
        this.loadingCommission = false
      }
    },
    async fetchAudit() {
      this.loadingAudit = true
      try {
        const data = await fetchAdminReferralActions(this.auditPage, this.auditPageSize)
        this.auditItems = data.items || []
        this.auditTotal = data.total || 0
      } catch (e) {
        this.$message.error(e.message || '加载审计失败')
      } finally {
        this.loadingAudit = false
      }
    },
    async grantPartner(isPartner) {
      const uid = Number(this.partnerUserId)
      const username = (this.partnerUsername || '').trim()
      if (!uid && !username) {
        this.$message.warning('请输入用户 ID 或用户名')
        return
      }
      const payload = { isPartner }
      if (uid) payload.userId = uid
      if (username) payload.username = username
      const res = await axios.post('/api/v1/admin/partner/grant', payload)
      if (res.data.code === 200) {
        this.$message.success(res.data.message)
        if (this.activeTab === 'audit') this.fetchAudit()
      } else {
        this.$message.error(res.data.message || '操作失败')
      }
    },
    async retryBonus(row) {
      try {
        await this.$confirm(`为绑定 #${row.id} 补发礼遇？`, '补发确认', { type: 'warning' })
      } catch {
        return
      }
      try {
        const res = await adminRetryReferralBonus(row.id)
        this.$message.success(res.message || '已补发')
        await this.fetchBindings()
      } catch (e) {
        this.$message.error(e.message || '补发失败')
      }
    },
    async revokeBinding(row) {
      try {
        await this.$confirm(
          `确定撤销绑定 #${row.id}？被推荐人将不可再绑礼券；已发礼遇不会自动冲正。`,
          '撤销绑定',
          { type: 'warning' }
        )
      } catch {
        return
      }
      const res = await axios.post('/api/v1/admin/referral/bindings/revoke', { bindingId: row.id })
      if (res.data.code === 200) {
        this.$message.success(res.data.message || res.data.data?.message || '已撤销')
        await this.fetchBindings()
        if (this.activeTab === 'audit') this.fetchAudit()
      } else {
        this.$message.error(res.data.message || '撤销失败')
      }
    }
  }
}
</script>

<style scoped>
.filter-input { width: 130px; }
.partner-grant { margin-top: 24px; padding-top: 16px; border-top: 1px solid #eee; }
.partner-grant__hint { margin: 0 0 12px; color: #909399; font-size: 13px; }
</style>
